/**
 * API service for Admin operations (Super Admin only)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.deepchat.se'

export interface Company {
  id: number
  name: string
  domain: string
  industry?: string
  subscription_status: string
  trial_end_date?: string
  max_agents?: number
  current_agent_count?: number
  owner_email?: string
  owner_name?: string
  owner_user_id?: number
  model_id?: number
  model_name?: string
  model_provider?: string
  is_active?: boolean
  billing_email?: string
  subscription_renews_at?: string
  stripe_customer_id?: string
  stripe_subscription_id?: string
  billing_period_start?: string
  billing_period_end?: string
  last_reminder_sent_at?: string
  reminder_count?: number
  payment_link?: string
  created_at?: string
}

export interface UpdateCompanyDetailsRequest {
  name?: string
  domain?: string
  industry?: string | null
  owner_first_name?: string | null
  owner_last_name?: string | null
  owner_email?: string | null
  billing_email?: string | null
  max_agents?: number
  subscription_status?: 'trial' | 'active' | 'suspended' | 'cancelled'
  is_active?: boolean
}

export interface AIModel {
  id: number
  name: string
  provider: string
  model_identifier: string
  base_url?: string
  is_active: boolean
  api_status?: 'online' | 'offline' | 'unknown'
  api_status_message?: string
  description?: string
  created_at: string
  updated_at: string
}

export interface CreateModelRequest {
  name: string
  provider: string
  model_identifier: string
  api_key?: string
  base_url?: string
  description?: string
}

export interface UpdateModelRequest {
  name?: string
  provider?: string
  model_identifier?: string
  api_key?: string
  base_url?: string
  description?: string
  is_active?: boolean
}

export interface EmailSettings {
  provider: string
  from_email: string
  from_name: string
  reply_to_email?: string
  sendgrid_api_key?: string
  smtp_host: string
  smtp_port: number
  smtp_username: string
  smtp_password?: string
  is_active: boolean
  has_sendgrid_api_key?: boolean
  has_smtp_password?: boolean
}

export interface SmtpServer {
  id: number
  name: string
  host: string
  port: number
  username: string
  password?: string
  encryption: 'tls' | 'starttls' | 'none'
  from_email: string
  from_name: string
  reply_to_email?: string
  is_active: boolean
  has_password?: boolean
  created_at: string
  updated_at: string
}

export interface EmailTemplate {
  id: number
  template_key: string
  name: string
  subject: string
  html_body: string
  text_body?: string
  sms_body?: string
  is_active: boolean
}

export interface PaymentSettings {
  stripe_mode: 'test' | 'live'
  stripe_test_secret_key?: string
  stripe_test_publishable_key?: string
  stripe_live_secret_key?: string
  stripe_live_publishable_key?: string
  has_stripe_test_secret_key?: boolean
  has_stripe_live_secret_key?: boolean
  stripe_secret_key?: string
  stripe_publishable_key?: string
  stripe_webhook_secret?: string
  has_stripe_secret_key?: boolean
  has_stripe_webhook_secret?: boolean
  default_price_id?: string
  trial_days: number
  renewal_reminder_days: number
  is_active: boolean
}

export interface SubscriptionPlan {
  id: number
  name: string
  description: string
  price_sek: number
  billing_period: 'monthly' | 'yearly'
  max_agents: number
  features: string[]
  stripe_price_id?: string
  is_active: boolean
  sort_order: number
  company_count?: number
  created_at?: string
  updated_at?: string
}

export type SubscriptionPlanPayload = Omit<SubscriptionPlan, 'id' | 'company_count' | 'created_at' | 'updated_at'>

export interface Order {
  id: number
  order_number: string
  company_id: number
  company_name?: string
  billing_email?: string
  owner_email?: string
  plan_id?: number
  plan_name?: string
  plan_billing_period?: 'monthly' | 'yearly'
  promo_code?: string
  amount_sek: number
  discount_percent: number
  billing_months: number
  billing_start?: string
  billing_end?: string
  payment_method?: string
  stripe_session_id?: string
  stripe_payment_intent?: string
  status: 'pending' | 'paid' | 'failed' | 'refunded'
  paid_at?: string
  created_at?: string
}

export interface SmsProvider {
  id: number
  name: string
  api_url: string
  http_method: 'POST' | 'GET'
  content_type: 'application/json' | 'application/x-www-form-urlencoded'
  phone_param: string
  message_param: string
  phone_prefix: string
  extra_params: string
  auth_header?: string
  is_active: boolean
  priority: number
  last_success_at?: string
  last_error?: string
  created_at: string
  updated_at: string
}

export interface SmsLog {
  id: number
  provider_id?: number
  provider_name?: string
  phone: string
  message: string
  template_key?: string
  status: 'pending' | 'sent' | 'failed'
  error_message?: string
  created_at: string
}

class AdminService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = localStorage.getItem('token')
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    }
  }

  async getCompanies(): Promise<Company[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to fetch companies')
    }

    const data = await response.json()
    return data.data || []
  }

  async getCompaniesWithModels(): Promise<Company[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies-with-models`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to fetch companies')
    }

    const data = await response.json()
    return data.data || []
  }

  async accessCompany(companyId: number): Promise<{ token: string; user: any; company: any }> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/access`, {
      method: 'POST',
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to access company')
    }

    const data = await response.json()
    return data.data
  }

  async getModels(): Promise<AIModel[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models`, {
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to fetch models')
    }

    const data = await response.json()
    return data.data || []
  }

  async createModel(request: CreateModelRequest): Promise<AIModel> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to create model')
    }

    const data = await response.json()
    return data.data
  }

  async updateModel(modelId: number, request: UpdateModelRequest): Promise<AIModel> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models/${modelId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to update model')
    }

    const data = await response.json()
    return data.data
  }

  async deleteModel(modelId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/models/${modelId}`, {
      method: 'DELETE',
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to delete model')
    }
  }

  async setCompanyModel(companyId: number, modelId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/model`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ model_id: modelId })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to set company model')
    }
  }

  async updateCompanyStatus(companyId: number, request: {
    is_active?: boolean
    subscription_status?: string
    subscription_renews_at?: string | null
    billing_period_start?: string | null
    billing_period_end?: string | null
    billing_email?: string | null
    max_agents?: number
    subscription_plan_id?: number | null
    mark_paid?: boolean
    billing_period?: 'monthly' | 'yearly'
  }): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/status`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to update company status')
    }
  }

  async updateCompanyDetails(companyId: number, request: UpdateCompanyDetailsRequest): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/details`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(request)
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to update company details')
    }
  }

  async getEmailSettings(): Promise<EmailSettings | null> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/email-settings`, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to load email settings')
    }
    const data = await response.json()
    return data.data || null
  }

  async saveEmailSettings(settings: Partial<EmailSettings>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/email-settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to save email settings')
    }
  }

  async sendTestEmail(to: string): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/email-settings/test`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ to })
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to send test email')
    }
  }

  async getSmtpServers(): Promise<SmtpServer[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/smtp-servers`, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to load SMTP servers')
    }
    const data = await response.json()
    return data.data || []
  }

  async createSmtpServer(server: Partial<SmtpServer>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/smtp-servers`, {
      method: 'POST',
      headers,
      body: JSON.stringify(server)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to add SMTP server')
    }
  }

  async updateSmtpServer(serverId: number, server: Partial<SmtpServer>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/smtp-servers/${serverId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(server)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to update SMTP server')
    }
  }

  async activateSmtpServer(serverId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/smtp-servers/${serverId}/activate`, {
      method: 'POST',
      headers
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to activate SMTP server')
    }
  }

  async deactivateSmtpServer(serverId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/smtp-servers/${serverId}/deactivate`, {
      method: 'POST',
      headers
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to deactivate SMTP server')
    }
  }

  async deleteSmtpServer(serverId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/smtp-servers/${serverId}`, {
      method: 'DELETE',
      headers
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to delete SMTP server')
    }
  }

  async getEmailTemplates(): Promise<EmailTemplate[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/email-templates`, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to load email templates')
    }
    const data = await response.json()
    return data.data || []
  }

  async saveEmailTemplate(template: EmailTemplate): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/email-templates/${encodeURIComponent(template.template_key)}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(template)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to save email template')
    }
  }

  async getPaymentSettings(): Promise<PaymentSettings | null> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/payment-settings`, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to load payment settings')
    }
    const data = await response.json()
    return data.data || null
  }

  async savePaymentSettings(settings: Partial<PaymentSettings>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/payment-settings`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(settings)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to save payment settings')
    }
  }

  async getPlans(): Promise<SubscriptionPlan[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/plans`, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to load plans')
    }
    const data = await response.json()
    return (data.data || []).map((plan: any) => ({
      ...plan,
      price_sek: Number(plan.price_sek || 0),
      max_agents: Number(plan.max_agents || 0),
      sort_order: Number(plan.sort_order || 0),
      company_count: Number(plan.company_count || 0),
      is_active: Boolean(plan.is_active),
      features: Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '[]')
    }))
  }

  async createPlan(plan: SubscriptionPlanPayload): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/plans`, {
      method: 'POST',
      headers,
      body: JSON.stringify(plan)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to create plan')
    }
  }

  async updatePlan(id: number, plan: Partial<SubscriptionPlanPayload>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/plans/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(plan)
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to update plan')
    }
  }

  async deletePlan(id: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/plans/${id}`, {
      method: 'DELETE',
      headers
    })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to delete plan')
    }
  }

  async getOrders(): Promise<Order[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/orders`, { headers })
    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.message || 'Failed to load orders')
    }
    const data = await response.json()
    return (data.data || []).map((order: any) => ({
      ...order,
      amount_sek: Number(order.amount_sek || 0),
      discount_percent: Number(order.discount_percent || 0),
      billing_months: Number(order.billing_months || 0)
    }))
  }

  async createStripeCheckout(companyId: number): Promise<string> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/stripe-checkout`, {
      method: 'POST',
      headers
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create Stripe checkout')
    }
    return data.data.url
  }

  async sendRenewalReminder(companyId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/send-renewal-reminder`, {
      method: 'POST',
      headers
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send renewal reminder')
    }
  }

  async sendTrialReminder(companyId: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/companies/${companyId}/send-trial-reminder`, {
      method: 'POST',
      headers
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send trial reminder')
    }
  }

  // SMS Gateway
  async getSmsProviders(): Promise<SmsProvider[]> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/sms-providers`, { headers })
    if (!response.ok) throw new Error('Failed to load SMS providers')
    const data = await response.json()
    return data.data || []
  }

  async createSmsProvider(provider: Partial<SmsProvider>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/sms-providers`, {
      method: 'POST', headers, body: JSON.stringify(provider)
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to create SMS provider')
    }
  }

  async updateSmsProvider(id: number, provider: Partial<SmsProvider>): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/sms-providers/${id}`, {
      method: 'PUT', headers, body: JSON.stringify(provider)
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to update SMS provider')
    }
  }

  async deleteSmsProvider(id: number): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/sms-providers/${id}`, {
      method: 'DELETE', headers
    })
    if (!response.ok) throw new Error('Failed to delete SMS provider')
  }

  async testSmsProvider(id: number, phone: string): Promise<void> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/sms-providers/${id}/test`, {
      method: 'POST', headers, body: JSON.stringify({ phone })
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send test SMS')
    }
  }

  async getSmsLogs(limit = 50, offset = 0): Promise<{ data: SmsLog[]; total: number }> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${API_BASE_URL}/api/admin/sms-logs?limit=${limit}&offset=${offset}`, { headers })
    if (!response.ok) throw new Error('Failed to load SMS logs')
    const data = await response.json()
    return { data: data.data || [], total: data.total || 0 }
  }
}

export const adminService = new AdminService()
